import { type DatePickerProps } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import CustomDatePicker from './CustomDatePicker';
import { useEffect, useState } from 'react';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import { getMuiDateFormat } from 'utils/helpers/locale-date-utils';
import { DATE_FORMAT } from 'utils/constants/dateTime';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props extends DatePickerProps<Dayjs> {
  format?: string;
  startDate: Date | null;
  endDate: Date | null;
  onFinalDateChange?: (range: [Date | null, Date | null]) => void;
  maxDaysFromStartDate?: number;
  wrap?: boolean;
  showDash?: boolean;
  autoOpenEnabled?: boolean;
}

const CustomDateRangePicker = ({
  format = DATE_FORMAT,
  startDate,
  endDate,
  onFinalDateChange,
  maxDate,
  maxDaysFromStartDate,
  wrap,
  showDash = true,
  autoOpenEnabled = true,
  ...props
}: Props) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [startDateValue, setStartDateValue] = useState<Dayjs | null>(
    startDate ? dayjs(startDate) : null
  );
  const [endDateValue, setEndDateValue] = useState<Dayjs | null>(
    endDate ? dayjs(endDate) : null
  );

  const [endDatePickerOpen, setEndDatePickerOpen] = useState(
    autoOpenEnabled ? false : undefined
  );

  const callFinalDateChange = (endDate: Dayjs | null) => {
    if (onFinalDateChange && endDate) {
      if (startDateValue) {
        onFinalDateChange([startDateValue.toDate(), endDate.toDate()]);
      }
    }
    if (autoOpenEnabled) {
      setEndDatePickerOpen(false);
    }
  };

  const [isInStartPicker, setIsInStartPicker] = useState(
    props?.views?.length ?? 0
  );

  const [isInEndPicker, setIsInEndPicker] = useState(props?.views?.length ?? 0);

  useEffect(() => {
    if (startDate) {
      setStartDateValue(dayjs(startDate));
    }
    if (endDate) {
      setEndDateValue(dayjs(endDate));
    }
  }, [startDate, endDate]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        width: 'fit-content',
        height: 'fit-content',
        flexWrap: wrap ? 'wrap' : 'nowrap',
      }}
    >
      <CustomDatePicker
        {...props}
        format={format ?? getMuiDateFormat()}
        value={startDateValue}
        // onClose={() => {
        //   setEndDatePickerOpen(true);
        // }}
        onChange={(date) => {
          setStartDateValue(date);
          setEndDateValue(null);
          // Notify parent that end date has been cleared
          if (onFinalDateChange) {
            onFinalDateChange([date ? date.toDate() : null, null]);
          }
          if (autoOpenEnabled) {
            // Only auto-open if the current picker has no more views to show
            if (isInStartPicker && isInStartPicker > 1) {
              setIsInStartPicker((prev) => prev - 1);
            } else {
              setEndDatePickerOpen(true);
              setIsInStartPicker(props?.views?.length ?? 0);
            }
          }
        }}
        maxDate={maxDate}
      />
      {showDash && (
        <p
          style={{
            color: palette.textPrimary,
            margin: '0',
          }}
        >
          -
        </p>
      )}
      <CustomDatePicker
        {...props}
        open={endDatePickerOpen}
        onOpen={() => {
          if (autoOpenEnabled) {
            setEndDatePickerOpen(true);
          }
        }}
        onClose={() => {
          if (autoOpenEnabled) {
            setEndDatePickerOpen(false);
          }
        }}
        format={format ?? getMuiDateFormat()}
        value={endDateValue}
        onChange={(date) => {
          setEndDateValue(date);
          if (autoOpenEnabled) {
            if (isInEndPicker && isInEndPicker > 1) {
              setIsInEndPicker((prev) => prev - 1);
            } else {
              callFinalDateChange(date);
              setIsInEndPicker(props?.views?.length ?? 0);
            }
          } else {
            callFinalDateChange(date);
          }
        }}
        minDate={startDateValue ?? undefined}
        maxDate={
          maxDaysFromStartDate
            ? dayjs(startDateValue).add(maxDaysFromStartDate, 'day')
            : maxDate
        }
      />
    </div>
  );
};

export default CustomDateRangePicker;
